/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { jsx } from '@atlaskit/css';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import { resolveSkillDisplayDescription } from '@atlassian/rovo-platform-ui-components/skills/resolve-skill-display-description';
import {
	SkillTag,
	type RovoSkillTagProps,
} from '@atlassian/rovo-platform-ui-components/skills/SkillTag';
import { ConnectedSkillTag } from '@atlassian/rovo-skills/ui/connected-skill-tag';

import { SearchInactiveContent } from '../chat-prompt-input/SearchInactiveContent';
import { useSearchMode } from '../chat-prompt-input/useSearchMode';

/**
 * Creates the `nodes.skillTag` definition used by extension manifests to render
 * skill chip inline extensions. When available, the skills map supplies the
 * complete metadata for ADF nodes that only contain a slug.
 *
 * Extracted so it can be shared between the full `useSkillsManifest` (which
 * also registers quick-insert items) and the lightweight
 * `useSkillTagExtensionProvider` (used as a Suspense fallback so skill chips
 * render immediately without waiting for the skills Relay query).
 */
export const createSkillTagNodeRenderer = (
	siteId?: string,
	disableSkillTagHoverCardInEditor: boolean = false,
	skillTagHoverCardMaxHeightInEditor?: number | string,
	skillsBySlug?: ReadonlyMap<string, Skill>,
) => ({
	skillTag: {
		type: 'inlineExtension' as const,
		render: async (): Promise<
			(props: { node: { parameters?: Record<string, unknown> } }) => React.ReactElement | null
		> =>
			function SkillTagNode({ node }: { node: { parameters?: Record<string, unknown> } }) {
				const searchMode = useSearchMode();
				const params = node.parameters;
				if (!params) {
					return null;
				}
				const skill = skillsBySlug?.get(params.slug as string);
				const skillTagProps: Record<string, unknown> = skill
					? {
							...params,
							...Object.fromEntries(
								Object.entries(skill).filter(([, value]) => value !== null && value !== undefined),
							),
							description: resolveSkillDisplayDescription(skill.helpText, skill.description),
						}
					: {
							...params,
							description: resolveSkillDisplayDescription(params.helpText, params.description),
						};
				// Fall back to the directory lookup when the available-skills response has no display metadata.
				if (!skill && siteId && !skillTagProps.color && !skillTagProps.iconName) {
					return (
						<SearchInactiveContent>
							<ConnectedSkillTag
								slug={skillTagProps.slug as string}
								siteId={siteId}
								disableHovercard={Boolean(searchMode) || disableSkillTagHoverCardInEditor}
								hovercardMaxHeight={skillTagHoverCardMaxHeightInEditor}
							/>
						</SearchInactiveContent>
					);
				}
				return (
					<SearchInactiveContent>
						<SkillTag
							{...(skillTagProps as RovoSkillTagProps)}
							disableHovercard={Boolean(searchMode) || disableSkillTagHoverCardInEditor}
							hovercardMaxHeight={skillTagHoverCardMaxHeightInEditor}
						/>
					</SearchInactiveContent>
				);
			},
	},
});
